import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../../components/ui/Button";
import DeleteModal from "../../../components/modals/DeleteModal";
import {
    getSoundLibraryAITypes,
    createSoundLibraryAIType,
    updateSoundLibraryAIType,
    deleteSoundLibraryAIType,
} from "../../../api/soundLibrary";
import { SoundLibraryAITypeResponse } from "../../../types/requestsOutputs";
import { useLanguage } from "../../../context/LanguageContext";
import { useNotification } from "../../../context/NotificationContext";
import { Formik, Form, Field, FormikHelpers } from "formik";
import * as Yup from "yup";
import FormGroup from "../../../components/common/FormGroup";
import { TextField } from "../../../components/common/FormFields";
import { Tooltip } from "@mui/material";
import { buildNameLocalizations, findLocalizationName } from "../../../utils/soundLibraryLocalizations";
import styles from "./SoundLibraryAdmin.module.css";

interface TypeFormValues {
    nameEN: string;
    nameFR: string;
    namePT: string;
    nameES: string;
    id?: string;
}

const TypesManagement: React.FC = () => {
    const { t: rawT, i18n } = useTranslation();
    const t = rawT as any;
    const { language } = useLanguage();
    const { showNotification } = useNotification();

    const [types, setTypes] = useState<SoundLibraryAITypeResponse[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

    const [isEditing, setIsEditing] = useState<boolean>(false);

    // Delete modal state
    const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
    const [typeToDelete, setTypeToDelete] = useState<{ id: string; name: string }>({ id: "", name: "" });

    // Form initial values
    const initialValues: TypeFormValues = {
        nameEN: "",
        nameFR: "",
        namePT: "",
        nameES: "",
        id: "",
    };

    // Validation schema
    const validationSchema = Yup.object({
        nameEN: Yup.string().required(t("nameInEnglishRequired")),
        nameFR: Yup.string().required(t("nameInFrenchRequired")),
    });

    const fetchTypes = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token") || "";
            const response = await getSoundLibraryAITypes({ token, language });
            setTypes(response || []);
        } catch (error) {
            console.error("Error fetching types:", error);
            showNotification({
                message: t("errorFetchingTypes"),
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    }, [language, t, showNotification]);

    useEffect(() => {
        fetchTypes();
    }, [fetchTypes]);

    const handleSubmit = useCallback(
        async (values: TypeFormValues, { resetForm, setSubmitting }: FormikHelpers<TypeFormValues>) => {
            try {
                const token = localStorage.getItem("token") || "";
                const data = {
                    name: values.nameEN,
                    localizations: buildNameLocalizations(values),
                };

                if (isEditing && values.id) {
                    await updateSoundLibraryAIType({ token, id: values.id, data });
                    showNotification({
                        message: t("typeUpdatedSuccessfully"),
                        type: "success",
                    });
                } else {
                    await createSoundLibraryAIType({ token, data });
                    showNotification({
                        message: t("typeCreatedSuccessfully"),
                        type: "success",
                    });
                }

                fetchTypes();
                resetForm();
                setIsEditing(false);
            } catch (error) {
                console.error("Error saving type:", error);
                showNotification({
                    message: isEditing ? t("errorUpdatingType") : t("errorCreatingType"),
                    type: "error",
                });
            } finally {
                setSubmitting(false);
            }
        },
        [fetchTypes, isEditing, showNotification, t],
    );

    const handleEdit = (
        type: SoundLibraryAITypeResponse,
        setFieldValue: FormikHelpers<TypeFormValues>["setFieldValue"],
    ) => {
        setIsEditing(true);

        setFieldValue("nameEN", findLocalizationName(type.localizations, "EN") || type.name || "");
        setFieldValue("nameFR", findLocalizationName(type.localizations, "FR"));
        setFieldValue("namePT", findLocalizationName(type.localizations, "PT"));
        setFieldValue("nameES", findLocalizationName(type.localizations, "ES"));
        setFieldValue("id", type.id);
    };

    const openDeleteModal = (type: SoundLibraryAITypeResponse) => {
        setTypeToDelete({
            id: type.id,
            name: type.name,
        });
        setDeleteModalOpen(true);
    };

    const handleDelete = async () => {
        setDeleteLoading(true);
        try {
            const token = localStorage.getItem("token") || "";
            await deleteSoundLibraryAIType({ token, id: typeToDelete.id });
            showNotification({
                message: t("typeDeletedSuccessfully"),
                type: "success",
            });
            fetchTypes();
            setDeleteModalOpen(false);
        } catch (error) {
            console.error("Error deleting type:", error);
            showNotification({
                message: t("errorDeletingType"),
                type: "error",
            });
        } finally {
            setDeleteLoading(false);
            setIsEditing(false);
        }
    };

    const sortedTypes = useMemo(() => {
        return [...types].sort((a, b) => a.name.localeCompare(b.name));
    }, [types]);

    return (
        <div className={styles.typesManagementContainer}>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                validateOnMount
                validateOnChange
                key={i18n.language}
            >
                {({ values, isSubmitting, resetForm, setFieldValue, errors }) => (
                    <>
                        <div className={styles.makerCard}>
                            <h3 className={styles.makerTitle}>{isEditing ? t("editType") : t("createType")}</h3>
                            <Form className={`types-form ${styles.langGrid}`}>
                                <FormGroup name="nameEN" size="small">
                                    <Field
                                        name="nameEN"
                                        component={TextField}
                                        placeholder={t("enterNameInEnglish")}
                                        disabled={isSubmitting}
                                    />
                                </FormGroup>

                                <FormGroup name="nameFR" size="small">
                                    <Field
                                        name="nameFR"
                                        component={TextField}
                                        placeholder={t("enterNameInFrench")}
                                        disabled={isSubmitting}
                                    />
                                </FormGroup>

                                <FormGroup name="namePT" size="small">
                                    <Field
                                        name="namePT"
                                        component={TextField}
                                        placeholder={t("enterNameInPortuguese")}
                                        disabled={isSubmitting}
                                    />
                                </FormGroup>

                                <FormGroup name="nameES" size="small">
                                    <Field
                                        name="nameES"
                                        component={TextField}
                                        placeholder={t("enterNameInSpanish")}
                                        disabled={isSubmitting}
                                    />
                                </FormGroup>

                                <div className={styles.langActions}>
                                    {isSubmitting ? (
                                        <div
                                            style={{
                                                width: "100px",
                                                height: "46px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <div
                                                className="loading-spinner"
                                                style={{ width: "24px", height: "24px" }}
                                            ></div>
                                        </div>
                                    ) : (
                                        <Tooltip
                                            title={
                                                Object.keys(errors).length > 0
                                                    ? t("formIncompleteTooltip", {
                                                          fields: Object.values(errors).join(", "),
                                                      })
                                                    : ""
                                            }
                                            arrow
                                        >
                                            <span>
                                                <Button
                                                    label={isEditing ? t("update") : t("create")}
                                                    variant="primary"
                                                    shape="default"
                                                    className={styles.createSoundBtn}
                                                    onClick={() => {
                                                        const formElement = document.querySelector(".types-form");
                                                        if (formElement) {
                                                            (formElement as HTMLFormElement).requestSubmit();
                                                        }
                                                    }}
                                                    disabled={isSubmitting || !values.nameEN || !values.nameFR}
                                                />
                                            </span>
                                        </Tooltip>
                                    )}
                                    {isEditing && (
                                        <Button
                                            label={t("cancel")}
                                            onClick={() => {
                                                resetForm();
                                                setIsEditing(false);
                                            }}
                                            variant="cancel"
                                            disabled={isSubmitting}
                                        />
                                    )}
                                </div>
                            </Form>
                        </div>
                        <div className="types-list-section">
                            <h3>{t("soundTypes")}</h3>

                            {loading ? (
                                <div className="loading-container">
                                    <div className="loading-spinner"></div>
                                </div>
                            ) : sortedTypes.length === 0 ? (
                                <div className={styles.noItemsMessage}>{t("noTypesFound")}</div>
                            ) : (
                                <div className={styles.typesAndCategoriesList}>
                                    {sortedTypes.map((type) => (
                                        <div key={type.id} className={styles.typesAndCategoriesItem}>
                                            <div className={styles.typesAndCategoriesLeft}>
                                                <div className={styles.typesAndCategoriesInfo}>
                                                    <h4>{type.name}</h4>
                                                    <p>
                                                        {type.localizations?.map((loc: any) => (
                                                            <span key={loc.id} className={styles.localizationBadge}>
                                                                {loc.language}: {loc.name}
                                                            </span>
                                                        ))}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className={styles.typesAndCategoriesActions}>
                                                <button
                                                    type="button"
                                                    className={styles.btnMod}
                                                    onClick={() => handleEdit(type, setFieldValue)}
                                                    disabled={isSubmitting || deleteLoading}
                                                >
                                                    {t("edit")}
                                                </button>
                                                <button
                                                    type="button"
                                                    className={styles.btnDel}
                                                    onClick={() => openDeleteModal(type)}
                                                    disabled={isSubmitting || deleteLoading}
                                                >
                                                    {t("delete")}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </Formik>

            <DeleteModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={handleDelete}
                message={t("deleteTypeWarning")}
                title={t("confirmDeleteType")}
            />
        </div>
    );
};

export default TypesManagement;
